// Push subscription helpers — registers the service worker, subscribes
// the PushManager, and POSTs the subscription to the backend.
//
// On iOS this only works if the PWA is installed via "Add to Home Screen"
// (PushManager is undefined in regular Safari tabs).

import { push } from './api.js';

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export function isStandalone() {
  // iOS uses navigator.standalone; modern browsers use display-mode media query.
  return window.navigator.standalone === true ||
         window.matchMedia?.('(display-mode: standalone)').matches;
}

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function arrayBufferToBase64Url(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('Push not supported on this device.');

  const registration = await navigator.serviceWorker.ready;
  let sub = await registration.pushManager.getSubscription();

  if (!sub) {
    const { publicKey } = await push.vapidKey();
    sub = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  }

  await push.subscribe(sub.endpoint, {
    p256dh: arrayBufferToBase64Url(sub.getKey('p256dh')),
    auth: arrayBufferToBase64Url(sub.getKey('auth'))
  });
  return sub;
}

export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const sub = await registration.pushManager.getSubscription();
  if (sub) {
    await push.unsubscribe(sub.endpoint);
    await sub.unsubscribe();
  }
}
